import React, { useState } from "react";
import { Checkbox, Divider, Space, Button } from "antd";
import { FilterFilled } from "@ant-design/icons";

// Generic single checkbox-list filter panel for antd <Table> columns —
// ported from the same component in BrandManager.js/User.js (Staff
// Management's "User Type"/"Customer Name(s)" filters) so every request
// list table (Graphics, Blog Write, ...) gets the same filter UI/UX instead
// of each page reinventing it. Replaces antd's default `filters` dropdown,
// whose Reset button only clears the checkboxes without confirming (a
// follow-up OK click was needed to actually apply the reset) — Reset here
// calls onApply([]) + close() together in one click.
export function CheckboxFilterDropdown({ options, initial, onApply, onClose, width = 200, maxHeight }) {
  const [selected, setSelected] = useState(initial || []);
  return (
    <div style={{ padding: 12, width }}>
      <Checkbox.Group
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          ...(maxHeight ? { maxHeight, overflowY: "auto" } : {}),
        }}
        value={selected}
        onChange={setSelected}
        options={options}
      />
      <Divider style={{ margin: "10px 0" }} />
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Button
          size="small"
          type="link"
          onClick={() => {
            setSelected([]);
            onApply([]);
            onClose();
          }}
        >
          Reset
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            onApply(selected);
            onClose();
          }}
        >
          OK
        </Button>
      </Space>
    </div>
  );
}

// Builds the `filterIcon`/`filterDropdown`/`onFilter`/`filteredValue` set
// for one antd <Table> column, client-side against whatever rows are
// already loaded (no backend params) — same shape BrandManager.js's
// "Presence" column uses. `getOptions` and `getValue` both receive the full
// row list so option sets can be derived dynamically (e.g. every distinct
// assignee currently in the table) rather than hardcoded.
export function buildCheckboxColumnFilter({
  rows,
  rowFilter,
  setRowFilter,
  key,
  getOptions,
  getValue,
}) {
  const selectedValues = rowFilter[key] || [];
  const options = getOptions(rows);
  return {
    filteredValue: selectedValues.length ? selectedValues : null,
    onFilter: (value, record) => {
      const recordValue = getValue(record);
      return Array.isArray(recordValue) ? recordValue.includes(value) : recordValue === value;
    },
    filterIcon: () => (
      <FilterFilled style={{ color: selectedValues.length ? "#1890ff" : undefined }} />
    ),
    filterDropdown: ({ close }) => (
      <CheckboxFilterDropdown
        options={options}
        initial={selectedValues}
        maxHeight={220}
        onApply={(vals) => setRowFilter((prev) => ({ ...prev, [key]: vals }))}
        onClose={close}
      />
    ),
  };
}

// Distinct, sorted { label, value } option list from a row array — the
// common case for building `getOptions` above (e.g. every distinct client
// name, assignee name, etc. actually present in the currently loaded rows).
export const distinctOptions = (rows, getRaw) => {
  const seen = new Map();
  rows.forEach((row) => {
    const raw = getRaw(row);
    const values = Array.isArray(raw) ? raw : [raw];
    values.forEach((v) => {
      const value = v ?? "";
      if (value && !seen.has(value)) seen.set(value, value);
    });
  });
  return [...seen.values()]
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map((value) => ({ label: value, value }));
};
